import classNames from 'classnames';
import React, { useCallback, useMemo } from 'react';
import type { DroppableProvided } from 'react-beautiful-dnd';
import { Droppable } from 'react-beautiful-dnd';
import { useIntl, FormattedMessage } from 'react-intl';
import { faCopy } from '@fortawesome/pro-light-svg-icons/faCopy';
import { faTrashCan } from '@fortawesome/pro-light-svg-icons/faTrashCan';

import { IconButton } from 'ui-components/components/button/icon-button';
import { FlexColumn, FlexRow, Gap } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';

import { useReadFromScale } from '../../../../../hooks/use-read-from-scale';
import { PACKAGE_ID_PREFIX } from '../../constants/drag-drop';
import { useMultiPackageModalContext } from '../../contexts/multi-package-modal-context';
import { getAssignedItemsForPackage, getMaxQuantityForChunk } from '../../helpers/assignment-helpers';
import packageIcon from '../../svg/package-icon.svg';
import { ItemCard } from '../item-card/item-card';
import { PackageDetailsSection } from './components/package-details-section/package-details-section';

import { messages } from './package-card-messages';
import style from './package-card.module.css';

import type { Package, OrderItem } from '../../types';
import type { Dimensions, Weight } from '../../../multi-package-modal/package-set';

type ScaleReadResult = {
  weight: Weight;
  dims?: Dimensions;
};

type PackageCardProps = {
  pack: Package;
  packageIndex: number;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onDuplicate: () => void;
  onRemove: () => void;
};

export const PackageCard = ({ pack, packageIndex, isExpanded, onToggleExpand, onDuplicate, onRemove }: PackageCardProps) => {
  const { items, itemQuantities, unassignItemFromPackage, updateChunkQuantity, changePackageDimensions, changeWeight } =
    useMultiPackageModalContext();

  const assignedItemsData = useMemo(() => getAssignedItemsForPackage(packageIndex, itemQuantities), [packageIndex, itemQuantities]);
  const { formatMessage } = useIntl();

  const assignedItems = useMemo(
    () =>
      assignedItemsData
        .map(({ itemId, chunkId, quantity }) => {
          const item = items.find((i) => i.fulfillmentPlanItemId === itemId);
          if (!item) {
            return null;
          }
          return {
            item,
            chunkId,
            quantity,
          };
        })
        .filter((entry): entry is { item: OrderItem; chunkId: string; quantity: number } => entry !== null),
    [assignedItemsData, items],
  );

  const hasItems = assignedItems.length > 0;

  const onScaleReadCb = useCallback(
    (result: ScaleReadResult) => {
      changeWeight(result.weight, packageIndex);
      if (result.dims) {
        changePackageDimensions(result.dims, packageIndex);
      }
    },
    [changeWeight, changePackageDimensions, packageIndex],
  );
  const onReadFromScale = useReadFromScale(onScaleReadCb);

  const elementId = `multi-package-dnd-${packageIndex}-`;

  return (
    <div>
      <FlexColumn className={classNames(style['package-card'])}>
        <FlexRow className={style['package-header']}>
          <Text
            textType={TextType.Label}
            isBold
            value={pack.packageTypeName ? messages.packageNumberWithName : messages.packageNumber}
            formattedMessageValues={{ number: packageIndex + 1, name: pack.packageTypeName }}
          />

          <FlexRow gap={Gap.Space8}>
            <IconButton ariaLabelMessageDescriptor={messages.copyPackageLabel} icon={faCopy} onClick={onDuplicate} />
            <IconButton
              ariaLabelMessageDescriptor={messages.removePackageLabel}
              icon={faTrashCan}
              onClick={onRemove}
              disabled={hasItems}
              disabledTooltipMessage={hasItems ? <FormattedMessage {...messages.removePackageDisabledTooltip} /> : undefined}
            />
          </FlexRow>
        </FlexRow>

        <PackageDetailsSection
          pack={pack}
          packageIndex={packageIndex}
          elementId={elementId}
          isExpanded={isExpanded}
          onToggleExpand={onToggleExpand}
          onReadFromScale={onReadFromScale}
        />

        <Droppable droppableId={`${PACKAGE_ID_PREFIX}${packageIndex}`}>
          {(provided: DroppableProvided) => (
            <div ref={provided.innerRef} {...provided.droppableProps} className={style['droppable-zone']}>
              {hasItems ? (
                <>
                  <FlexColumn gap={Gap.Space16}>
                    {assignedItems.map(({ item, chunkId, quantity }, index) => (
                      <ItemCard
                        key={chunkId}
                        item={item}
                        chunkId={chunkId}
                        index={index}
                        quantity={quantity}
                        maxQuantity={getMaxQuantityForChunk(item.fulfillmentPlanItemId, chunkId, itemQuantities)}
                        onQuantityChange={updateChunkQuantity}
                        rightAction={
                          <IconButton
                            ariaLabelMessageDescriptor={messages.removeItemLabel}
                            icon={faTrashCan}
                            onClick={() => unassignItemFromPackage(item.fulfillmentPlanItemId, chunkId)}
                          />
                        }
                      />
                    ))}
                  </FlexColumn>
                  <div>{provided.placeholder}</div>
                </>
              ) : (
                <FlexColumn gap={Gap.Space12} className={style['empty-state']}>
                  <img src={packageIcon} alt={formatMessage(messages.packageIconAlt)} className={style.icon} />
                  <Text isBold textType={TextType.Label} value={messages.noItemsAssigned} />
                  <Text textType={TextType.Caption} value={messages.dragItemsHere} />
                </FlexColumn>
              )}
            </div>
          )}
        </Droppable>
      </FlexColumn>
    </div>
  );
};
